import { TwoColumnSubRouter } from 'components/core/TwoColumnSubRouter'
import type { QueryParams, Store } from 'mobx-router'
import { Route } from 'mobx-router'
import {
  AdminCatalogsLoader,
  AdminDashboardLoader,
  AdminLibraryLoader,
  AdminSlideDecksLoader,
  AdminSlideDecksItemLoader,
  AdminGlobalAuthorsLoader,
  AdminGlobalAssetsLoader,
  AdminTeachingPlanLoader,
  AdminUsersLoader,
  AdminDemosLoader,
  AdminInstitutionsLoader,
  AdminInspectorLoader,
  AdminPromptsLoader,
} from 'pages/admin/AdminLoader'
import { AvTestEntrypoint } from 'pages/av-test/AvTestEntrypoint'
import { HomePage } from 'pages/home/HomePage'
import { InvitationTokenPage } from 'pages/invitation/InvitationTokenPage'
import { LoginWithPassword } from 'pages/login/LoginWithPassword'
import { LogoutPage } from 'pages/login/LogoutPage'
import { OnboardingPage } from 'pages/onboarding/OnboardingPage'
import { ProfilePage } from 'pages/profile/ProfilePage'
import { PodcastsPage } from 'pages/student/podcasts/PodcastsPage'
import { Suspense, lazy } from 'react'
import { inLocalDev, isProdOrBeta } from './environment'
import { WelcomeRedirect } from 'pages/home/WelcomeRedirect'
import { RoleGuard } from './RoleGuard'
import { ContentLibraryGuard } from './ContentLibraryGuard'
import { ExternalDemoGuard } from './ExternalDemoGuard'
import { ExternalDemosLoader } from 'pages/external/ExternalLoader'
import { UserProfileRole } from '@breakoutlearning/firebase-repository/types'
import { DemoInvitationPage } from 'pages/demo/invitation/DemoInvitationPage'
import { DemoWelcomePage } from 'pages/demo/welcome/DemoWelcomePage'
import { LoginWithToken } from 'pages/login/LoginWithToken'
import { MagicLinkPage } from 'pages/magic-link/MagicLinkPage'
import { LtiConfigurationPage } from 'pages/lti/ConfigurationPage'
import { StudentAssignmentPendingPage } from 'pages/student/assignment/StudentAssignmentPendingPage'
import { InstructorLoader } from 'pages/instructor/InstructorLoader'
import { OrganizationsLoader } from 'pages/organizations/OrganizationsLoader'
import { LtiLoaderPage } from 'pages/lti/LoaderPage'
import { AlreadyImportedPage } from 'pages/lti/AlreadyImportedPage'
import { LtiErrorPage } from 'pages/lti/LtiErrorPage'
import { SectionEndedPage } from 'pages/lti/SectionEndedPage'
import { EditActivityPage } from 'pages/lti/EditActivityPage'
import { SlideDeckReview } from 'pages/instructor/slide_deck/SlideDeckReview'
import { AdminServerTestPage } from 'pages/admin/test-job/AdminServerTestPage'

// eslint-disable-next-line react-refresh/only-export-components
const GuidedUpdatePage = lazy(
  () => import('pages/guided-update/GuidedUpdatePage')
)
// Lazy-loaded to keep livekit-client out of the main bundle
// eslint-disable-next-line react-refresh/only-export-components
const MeetingEntrypoint = lazy(() =>
  import('pages/meeting/MeetingEntrypoint').then((m) => ({
    default: m.MeetingEntrypoint,
  }))
)
// eslint-disable-next-line react-refresh/only-export-components
const MeetingDevMode = lazy(() =>
  import('pages/meeting/MeetingDevMode').then((m) => ({
    default: m.MeetingDevMode,
  }))
)
// eslint-disable-next-line react-refresh/only-export-components
const DemoWaitingRoomPage = lazy(() =>
  import('pages/demo/meeting/DemoWaitingRoomPage').then((m) => ({
    default: m.DemoWaitingRoomPage,
  }))
)

export type RouteType = Route<
  Store,
  Record<string, string | number | boolean | undefined> | undefined,
  QueryParams
>

// Public routes are accessible without authentication
export class PublicRoute extends Route<
  Store,
  Record<string, string | number | boolean | undefined> | undefined,
  QueryParams
> {}

const twoColumn = <TwoColumnSubRouter />
export const routes: Record<string, RouteType> = {}
const subRoutes: Record<string, JSX.Element> = {}

routes.loginWithPassword = new PublicRoute({
  path: '/login-with-password',
  component: <LoginWithPassword />,
  title: 'loginWithPassword',
})
routes.loginWithToken = new PublicRoute({
  path: '/login/token',
  component: <LoginWithToken />,
  title: 'loginWithToken',
})

routes.magicLink = new PublicRoute({
  path: '/magic-link',
  component: <MagicLinkPage />,
  title: 'magicLink',
})

routes.demoInvitation = new PublicRoute({
  path: '/demo/invitation/:invitationId',
  component: <DemoInvitationPage />,
  title: 'demoInvitation',
})

routes.avTest = new Route({
  path: '/av-test',
  component: <AvTestEntrypoint />,
  title: 'avTest',
})

routes.demoWaitingRoom = new Route({
  path: '/demo/meeting/:roomId',
  component: (
    <Suspense fallback={null}>
      <DemoWaitingRoomPage />
    </Suspense>
  ),
  title: 'demoWaitingRoom',
})

routes.demoWelcome = new Route({
  path: '/demo/welcome',
  component: <DemoWelcomePage />,
  title: 'demoWelcome',
})

routes.invitationToken = new Route({
  path: '/invitation/:token',
  component: <InvitationTokenPage />,
  title: 'invitationToken',
})

routes.podcasts = new Route({
  path: '/podcast',
  component: <PodcastsPage />,
  title: 'podcasts',
})

routes.logout = new Route({
  path: '/logout',
  component: <LogoutPage />,
  title: 'logout',
})

routes.meeting = new Route({
  path: '/meeting/:roomId',
  component: (
    <Suspense fallback={null}>
      <MeetingEntrypoint />
    </Suspense>
  ),
  title: 'meeting',
})

routes.meetingDevMode = new Route({
  path: '/admin/devmode/:roomId',
  component: (
    <RoleGuard
      roles={[
        UserProfileRole.admin,
        UserProfileRole.editor,
        UserProfileRole.author,
        UserProfileRole.corre,
      ]}
    >
      <Suspense fallback={null}>
        <MeetingDevMode />
      </Suspense>
    </RoleGuard>
  ),
  title: 'meetingDevMode',
})

routes.onboarding = new Route({
  path: '/onboarding',
  component: <OnboardingPage />,
  title: 'onboarding',
})

routes.ltiConfiguration = new Route({
  path: '/lti/configuration/:sectionId/:assignmentId',
  component: <LtiConfigurationPage />,
  title: 'ltiConfiguration',
})

routes.ltiLoaderPage = new PublicRoute({
  path: '/lti/loader',
  component: <LtiLoaderPage />,
  title: 'ltiLoaderPage',
})

routes.ltiAlreadyImported = new PublicRoute({
  path: '/lti/already-imported',
  component: <AlreadyImportedPage />,
  title: 'ltiAlreadyImported',
})

routes.ltiError = new PublicRoute({
  path: '/lti/error',
  component: <LtiErrorPage />,
  title: 'ltiError',
})

routes.ltiSectionEnded = new PublicRoute({
  path: '/lti/section-ended',
  component: <SectionEndedPage />,
  title: 'ltiSectionEnded',
})

routes.ltiEditActivity = new PublicRoute({
  path: '/lti/edit-activity',
  component: <EditActivityPage />,
  title: 'ltiEditActivity',
})

routes.guidedUpdate = new PublicRoute({
  path: '/update',
  component: (
    <Suspense fallback={<div>Loading...</div>}>
      <GuidedUpdatePage />
    </Suspense>
  ),
  title: 'guidedUpdate',
})

// Welcome is a dart deprecated route, but are adding it as a redirect to home.
routes.welcome = new Route({
  path: '/welcome',
  component: <WelcomeRedirect />,
  title: 'welcome',
})

function addTwoColumnRoute(
  name: string,
  path: string,
  component: JSX.Element,
  options?: Omit<
    ConstructorParameters<typeof Route>[0],
    'path' | 'component' | 'title'
  >
) {
  const route = new Route({
    path: path,
    component: twoColumn,
    title: name,
    ...options,
  })
  routes[name] = route
  subRoutes[name] = component
}

// The HomePage controller handles home pages, but it also needs to handle
// the student experience - we need this because we need to keep a single component
// for transitions - if you switch between HomePage and StudentLibrary, the
// mount point changes and the library gets disposed and re-created, which is messing
// with cubits and transitions.
const homePageOrStudentLibrary = <HomePage />
addTwoColumnRoute('home', '/', homePageOrStudentLibrary)

addTwoColumnRoute(
  'assignmentPending',
  '/assignment-pending/:sectionId/:assignmentId',
  <StudentAssignmentPendingPage />
)

addTwoColumnRoute(
  'assignmentWithRoom',
  '/assignment/:sectionId/:assignmentId/:roomStateId',
  homePageOrStudentLibrary
)

addTwoColumnRoute(
  'assignment',
  '/assignment/:sectionId/:assignmentId',
  homePageOrStudentLibrary
)

//todo(alex): should authors/editors get here?
addTwoColumnRoute(
  'instructorLibrary',
  '/instructor/library',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.editor,
      UserProfileRole.author,
    ]}
  >
    <InstructorLoader page="instructorLibrary" />
  </RoleGuard>
)

// M1 prototype: Postgres-backed catalog page. The page itself reads the
// `useV2SlideDeckCatalog` feature flag and redirects to v1 when it's off,
// so the URL is always safe to share.
addTwoColumnRoute(
  'instructorLibraryV2',
  '/instructor/library/v2',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.editor,
      UserProfileRole.author,
    ]}
  >
    <InstructorLoader page="instructorLibraryV2" />
  </RoleGuard>
)

//todo(alex): should authors/editors get here?
addTwoColumnRoute(
  'instructorSlideDeck', // instructor, ta
  '/instructor/slide-deck/:slideDeckId',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.editor,
      UserProfileRole.author,
    ]}
  >
    <InstructorLoader page="instructorSlideDeck" />
  </RoleGuard>
)

addTwoColumnRoute(
  'instructorStudentView',
  '/instructor/home',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.editor,
      UserProfileRole.author,
    ]}
  >
    {homePageOrStudentLibrary}
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorClass', // instructor, ta
  '/instructor/classes/:id',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorClass" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorClassAssignment', // instructor, ta
  '/instructor/classes/:id/:assignmentId',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorClassAssignment" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorClasses',
  '/instructor/classes',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorClasses" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorAssistants',
  '/instructor/assistants',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorAssistants" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorAssistantsInvite',
  '/instructor/assistants/invite',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorAssistantsInvite" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorAssistantsConfigureAssistant',
  '/instructor/assistants/assistantId/:id/configure',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorAssistantsConfigureAssistant" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorAssistantsConfigureInvitation',
  '/instructor/assistants/invitationId/:id/configure',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorAssistantsConfigureInvitation" />
  </RoleGuard>
)
addTwoColumnRoute(
  'instructorTeachingPlan',
  '/instructor/nextbooks/:catalogId/:nextbookId',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorTeachingPlan" />
  </RoleGuard>
)
// Redirect:
addTwoColumnRoute(
  'instructorTeachingPlanLegacy',
  '/instructor/teaching_plan/:catalogId/:teachingPlanId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      const catalogId = params?.catalogId
      const teachingPlanId = params?.nextbookId || params?.teachingPlanId
      store.router.goTo(routes['instructorTeachingPlan'], {
        catalogId,
        teachingPlanId,
      })
    },
  }
)
// Redirect:
addTwoColumnRoute(
  'instructorTeachingPlanLegacy',
  '/instructor/teaching_plan/:catalogId/:nextbookId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      const catalogId = params?.catalogId
      const teachingPlanId = params?.nextbookId || params?.teachingPlanId
      store.router.goTo(routes['instructorTeachingPlan'], {
        catalogId,
        teachingPlanId,
      })
    },
  }
)
// Redirect:
addTwoColumnRoute(
  'instructorTeachingPlanLegacy',
  '/instructor/nextbooks/:catalogId/:teachingPlanId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      const catalogId = params?.catalogId
      const teachingPlanId = params?.nextbookId || params?.teachingPlanId
      store.router.goTo(routes['instructorTeachingPlan'], {
        catalogId,
        teachingPlanId,
      })
    },
  }
)

addTwoColumnRoute(
  'instructorOrgPicker',
  '/instructor/org-select/:institutionId',
  <RoleGuard
    roles={[
      UserProfileRole.instructor,
      UserProfileRole.ta,
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <InstructorLoader page="instructorOrgPicker" />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminDashboard',
  '/admin/dashboard',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.corre]}>
    <AdminDashboardLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminTeachingPlan',
  '/admin/catalogs/:catalogId/nextbooks/:nextbookId',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.editor,
      UserProfileRole.corre,
    ]}
  >
    <AdminTeachingPlanLoader />
  </RoleGuard>
)
// Redirect:
addTwoColumnRoute(
  'adminTeachingPlanLegacy',
  '/admin/catalogs/:catalogId/nextbooks/:teachingPlanId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      store.router.goTo(routes.adminTeachingPlan, {
        catalogId: params?.catalogId,
        nextbookId: params?.teachingPlanId,
      })
    },
  }
)
// Redirect:
addTwoColumnRoute(
  'adminTeachingPlanLegacy',
  '/admin/catalogs/:catalogId/teaching-plans/:nextbookId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      store.router.goTo(routes.adminTeachingPlan, {
        catalogId: params?.catalogId,
        nextbookId: params?.nextbookId,
      })
    },
  }
)
// Redirect:
addTwoColumnRoute(
  'adminTeachingPlanLegacy',
  '/admin/catalogs/:catalogId/teaching-plans/:teachingPlanId',
  <div />,
  {
    onEnter: (_route, params, store) => {
      store.router.goTo(routes.adminTeachingPlan, {
        catalogId: params?.catalogId,
        nextbookId: params?.teachingPlanId,
      })
    },
  }
)

addTwoColumnRoute(
  'adminLibrary',
  '/admin/library',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <AdminLibraryLoader />
  </RoleGuard>
)
addTwoColumnRoute(
  'adminCatalogs',
  '/admin/catalogs',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.editor,
      UserProfileRole.corre,
    ]}
  >
    <AdminCatalogsLoader />
  </RoleGuard>
)
addTwoColumnRoute(
  'adminCatalog',
  '/admin/catalogs/:catalogId',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.editor,
      UserProfileRole.corre,
    ]}
  >
    <AdminCatalogsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminSlideDeck',
  '/admin/slide-deck/:slideDeckId',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.author,
      UserProfileRole.editor,
    ]}
  >
    <AdminLibraryLoader />
  </RoleGuard>
)

// Content Library — a new admin section (Slide Decks + Global Authors + Global
// Assets) reached from the "Content Library" sidebar popover. Admin-only,
// behind the contentLibraryEnabled flag, with a super-admin override. All three
// pages share ContentLibraryGuard so the gate is one place.
addTwoColumnRoute(
  'adminSlideDecks',
  '/admin/slide-decks',
  <ContentLibraryGuard>
    <AdminSlideDecksLoader />
  </ContentLibraryGuard>
)
// Alternate entry to the existing slide-deck editor, reached from the new list.
// Its "Experiences" breadcrumb returns to /admin/slide-decks, restoring the
// list's filters from the `return` query param the list encodes on the way in.
addTwoColumnRoute(
  'adminSlideDecksItem',
  '/admin/slide-decks/:slideDeckId',
  <ContentLibraryGuard>
    <AdminSlideDecksItemLoader />
  </ContentLibraryGuard>
)
addTwoColumnRoute(
  'adminGlobalAuthors',
  '/admin/global-authors',
  <ContentLibraryGuard>
    <AdminGlobalAuthorsLoader />
  </ContentLibraryGuard>
)
addTwoColumnRoute(
  'adminGlobalAssets',
  '/admin/global-assets',
  <ContentLibraryGuard>
    <AdminGlobalAssetsLoader />
  </ContentLibraryGuard>
)

addTwoColumnRoute(
  'adminUsers',
  '/admin/users',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.corre]}>
    <AdminUsersLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminDemos',
  '/admin/demos',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.corre]}>
    <AdminDemosLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'externalDemos',
  '/external/demos',
  <ExternalDemoGuard>
    <ExternalDemosLoader />
  </ExternalDemoGuard>
)

addTwoColumnRoute(
  'adminInstitutions',
  '/admin/institutions',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.corre]}>
    <AdminInstitutionsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminPrompts',
  '/admin/prompts/:promptType?',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.editor]}>
    <AdminPromptsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminRubric',
  '/admin/prompts/:promptType/:typeId',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.editor]}>
    <AdminPromptsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminRubricVersion',
  '/admin/prompts/:promptType/:typeId/:versionId',
  <RoleGuard roles={[UserProfileRole.admin, UserProfileRole.editor]}>
    <AdminPromptsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminServerTest',
  '/admin/test-job',
  <RoleGuard roles={[UserProfileRole.admin]}>
    <AdminServerTestPage />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminInspector',
  '/admin/inspector',
  <RoleGuard roles={[UserProfileRole.admin]}>
    <AdminInspectorLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminInspectorRoom',
  '/admin/inspector/room/:roomId',
  <RoleGuard roles={[UserProfileRole.admin]}>
    <AdminInspectorLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'adminInspectorUser',
  '/admin/inspector/user/:userId',
  <RoleGuard roles={[UserProfileRole.admin]}>
    <AdminInspectorLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'organizations',
  '/organizations',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.instructor,
    ]}
  >
    <OrganizationsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'organization',
  '/organizations/:organizationId',
  <RoleGuard
    roles={[
      UserProfileRole.admin,
      UserProfileRole.corre,
      UserProfileRole.instructor,
    ]}
  >
    <OrganizationsLoader />
  </RoleGuard>
)

addTwoColumnRoute(
  'slideDeckReview',
  '/review/:slideDeckId',
  <SlideDeckReview />
)

// replaced `/profile` with `/settings`.
// Maintain old URL as redirect for backwards compatibility.
addTwoColumnRoute('settings', '/settings', <ProfilePage />)
addTwoColumnRoute('profile', '/profile', <div />, {
  onEnter: (_, __, store) => store.router.goTo(routes['settings']),
})

if (!isProdOrBeta()) {
  const DesignSystemEntrypoint = lazy(
    () => import('pages/design-system/DesignSystemPage')
  )
  const designSystemMount = (
    <Suspense fallback={<div>Loading...</div>}>
      <DesignSystemEntrypoint />
    </Suspense>
  )

  if (inLocalDev()) {
    routes.designSystemDev = new PublicRoute({
      title: 'designSystemDev',
      component: designSystemMount,
      path: '/design-system-dev',
    })
  }

  addTwoColumnRoute('designSystem', '/design-system', designSystemMount)
}

if (!isProdOrBeta()) {
  const DatepickerDemoEntrypoint = lazy(
    () => import('pages/datepicker-demo/DatepickerDemoPage')
  )
  routes.datepickerDemo = new PublicRoute({
    title: 'datepickerDemo',
    component: (
      <Suspense fallback={<div>Loading...</div>}>
        <DatepickerDemoEntrypoint />
      </Suspense>
    ),
    path: '/datepicker-demo',
  })
}

export function getRouteName(route: RouteType | undefined) {
  if (!route) return 'home'
  return route.title
}

export function getSubRouteComponent(routeName: string | undefined) {
  return subRoutes[routeName || 'home']
}

export function buildUrl(
  routeTitle: string,
  params: QueryParams,
  queryParams: QueryParams
) {
  const route = routes[routeTitle]

  if (!route) {
    throw new Error(`Route ${routeTitle} not found`)
  }

  let path = route.path
  for (const key in params) {
    const value = params[key]
    if (value !== undefined) {
      path = path.replace(`:${key}`, value.toString())
    }
  }

  const actualQueryParams: Record<string, string> = {}
  for (const key in queryParams) {
    const value = queryParams[key]
    if (value !== undefined) {
      actualQueryParams[key] = value.toString()
    }
  }

  const query = new URLSearchParams(actualQueryParams)

  const queryStr = query.toString()

  if (queryStr) {
    path += '?' + queryStr
  }

  return path
}
